import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import buildRoute from 'components/buildRoute';
import PageHeader from 'components/PageHeader';
import TableContainerWithScrollShadow from 'components/TableContainerWithScrollShadow';
import { fetchList, fetchRecord, createRecord, updateRecord } from '../../registryApi';

const OrganizationFormPage = () => {
  const navigate = useNavigate();
  const { projectId, orgId } = useParams();
  const isEditing = !!orgId;

  const [formData, setFormData] = useState({
    name: '',
    members: [], // [{ id, email }] — id is the registry profile id
  });
  const [registryUsers, setRegistryUsers] = useState([]);
  const [newUsers, setNewUsers] = useState([]); // user objects or typed/pasted email strings
  const [pickerError, setPickerError] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEditing);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    fetchRegistryUsers();
    if (isEditing) {
      fetchOrganization();
    }
  }, [isEditing, orgId]);

  const fetchRegistryUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await fetchList('/registry/users');
      setRegistryUsers(data || []);
    } catch (error) {
      console.error('Error fetching registry users:', error);
      setRegistryUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchOrganization = async () => {
    try {
      const data = await fetchRecord('/registry/organizations', orgId);
      setFormData({
        name: data.name || '',
        members: data.member_details || [],
      });
    } catch (error) {
      console.error('Error fetching organization:', error);
      alert('Failed to load organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const memberIds = new Set(formData.members.map((m) => String(m.id)));

  // Users may belong to multiple orgs (the c3/c4 case), so the picker offers
  // any community member not already in this org's list. The users API returns
  // community-member profiles only (full-access AI staff are never members).
  const availableUsers = registryUsers.filter((user) => !memberIds.has(String(user.id)));

  const userOptionLabel = (user) => {
    const orgs = (user.memberships || []).map((m) => m.organization_name);
    return `${user.email} — ${orgs.length ? `(${orgs.join(', ')})` : '(unassigned)'}`;
  };

  const resolveToUser = (entry) => {
    if (typeof entry !== 'string') return entry;
    const email = entry.trim();
    return availableUsers.find((u) => u.email.toLowerCase() === email.toLowerCase()) || null;
  };

  const handleAddUsers = () => {
    if (!newUsers.length) return;
    const additions = [];
    const invalid = [];

    newUsers.forEach((entry) => {
      const user = resolveToUser(entry);
      if (user && !memberIds.has(String(user.id))) {
        additions.push({ id: user.id, email: user.email });
      } else if (!user) {
        invalid.push(typeof entry === 'string' ? entry : entry.email);
      }
    });

    if (additions.length > 0) {
      setFormData({ ...formData, members: [...formData.members, ...additions] });
    }
    setPickerError(
      invalid.length > 0
        ? `The following emails do not match an addable user: ${invalid.join(', ')}`
        : ''
    );
    setNewUsers([]);
  };

  const handlePickerPaste = (event) => {
    const pastedText = event.clipboardData.getData('text');
    if (!pastedText.includes(',') && !pastedText.includes('\n')) return;

    event.preventDefault();
    const emails = pastedText
      .split(/[,\n]/)
      .map((email) => email.trim())
      .filter(Boolean);

    const matched = [];
    const invalid = [];
    emails.forEach((email) => {
      const user = availableUsers.find((u) => u.email.toLowerCase() === email.toLowerCase());
      const alreadySelected = newUsers.some(
        (entry) => resolveToUser(entry)?.email.toLowerCase() === email.toLowerCase()
      );
      if (user && !alreadySelected) {
        matched.push(user);
      } else if (!user) {
        invalid.push(email);
      }
    });

    if (matched.length > 0) {
      setNewUsers([...newUsers, ...matched]);
    }
    setPickerError(
      invalid.length > 0
        ? `The following emails do not match an addable user: ${invalid.join(', ')}`
        : ''
    );
  };

  const handleRemoveMember = (userId) => {
    setFormData({
      ...formData,
      members: formData.members.filter((m) => String(m.id) !== String(userId)),
    });
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Organization name is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const dataToSave = {
      name: formData.name,
      members: formData.members.map((m) => m.id),
    };

    try {
      if (isEditing) {
        await updateRecord('/registry/organizations', orgId, dataToSave);
      } else {
        await createRecord('/registry/organizations', dataToSave);
      }
      navigate(`/projects/${projectId}/registry/admin?tab=0`);
    } catch (error) {
      console.error('Error saving organization:', error);
      alert('Failed to save organization. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}/registry/admin?tab=0`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title={isEditing ? formData.name || 'Organization' : 'Create Organization'}
        subtitle="Organization details and user assignments"
      />

      <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 3 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CorporateFareIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Organization Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Organization Name"
                placeholder="Organization name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                size="small"
                InputLabelProps={{
                  shrink: true,
                  sx: { color: 'primary.main', fontWeight: 600 },
                }}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PeopleOutlineIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Users
            </Typography>
          </Box>

          <TableContainerWithScrollShadow sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No users assigned
                    </TableCell>
                  </TableRow>
                ) : (
                  formData.members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        {member.email}
                        {member.is_active === false && (
                          <Chip label="Deactivated" size="small" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveMember(member.id)}
                          aria-label="Remove user"
                          title="Remove user"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainerWithScrollShadow>

          {pickerError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPickerError('')}>
              {pickerError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Autocomplete
              multiple
              freeSolo
              disableCloseOnSelect
              options={availableUsers.filter(
                (user) =>
                  !newUsers.some((entry) => resolveToUser(entry)?.id === user.id)
              )}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : userOptionLabel(option)
              }
              value={newUsers}
              onChange={(event, newValue) => {
                setNewUsers(newValue);
                setPickerError('');
              }}
              loading={loadingUsers}
              fullWidth
              size="small"
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={typeof option === 'string' ? option : option.email}
                      {...chipProps}
                      size="small"
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add Users"
                  placeholder="Type to search, or paste comma-separated emails"
                  onPaste={handlePickerPaste}
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: 'primary.main', fontWeight: 600 },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="outlined"
              onClick={handleAddUsers}
              disabled={!newUsers.length}
              sx={{ minWidth: 130, py: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              + Add Users
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Users may belong to multiple organizations (e.g. one person's c3 and c4 entities).
            Members see their org's experiments; sharing with other organizations is managed
            per experiment in the Access tab.
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
          <Button variant="text" onClick={handleCancel}>
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  );
};

const routeArgs = {
  element: <OrganizationFormPage />,
};

export default buildRoute(routeArgs);
